import React, { useState } from "react";
import TripMap from "../components/tripMap/TripMap";
import { DayPills, StopBadge, TripPlanDay, stopTimes } from "../components/tripMap/TripMapParts";
import { ageLabel, canChangePickup, formatClock, sinceLabel } from "../components/tripMap/tripMapModel";
import { formatDateDMY, formatDateTimeDMY12 } from "../utils/dateFormat";

/**
 * The customer trip page once the policy is accepted: the trip map fills the top of the screen and
 * the rest of the page slides up over it like a sheet, opening with what is happening right now.
 * On a laptop the map sits on the left and the page on the right.
 */
export default function TrackingLiveTrip({
  tracking,
  model,
  now,
  emergencyShown,
  refreshing,
  onRefresh,
  onCopyLink,
  onGuide,
  onEditPickup,
  children,
}) {
  const [dayFilter, setDayFilter] = useState(() => Math.max(model?.currentDayIndex ?? 0, 0));
  const hasMap = Boolean(model?.hasMapContent);
  const live = liveStatus(tracking, model);

  return (
    <div className={`trip-live ${hasMap ? "" : "is-mapless"}`}>
      <div className="trip-live-map" data-tour="driver-location">
        {hasMap ? (
          <TripMap
            model={model}
            dayFilter={dayFilter}
            fitPadding={{ topLeft: [28, 150], bottomRight: [60, 56] }}
            controlsStyle={{ top: 150 }}
          />
        ) : null}
        <div className="trip-live-overlay">
          <div className="trip-live-topbar" data-tour="page-actions">
            <img src="/img/icon.png" alt="Indian Odyssey" />
            <div className="trip-live-title">
              <strong>{tracking.tripTitle || `Booking #${tracking.bookingId}`}</strong>
              <span>
                <span className={`trip-live-dot ${live.on ? "" : "is-stale"}`} />
                {live.text}
              </span>
            </div>
            <button type="button" className="trip-live-icon-btn" aria-label="Refresh" onClick={onRefresh} disabled={refreshing}>
              <i className={`bi ${refreshing ? "bi-hourglass-split" : "bi-arrow-clockwise"}`} aria-hidden="true" />
            </button>
            <button type="button" className="trip-live-icon-btn" aria-label="Copy link" onClick={onCopyLink}>
              <i className="bi bi-share" aria-hidden="true" />
            </button>
            <button type="button" className="trip-live-icon-btn" aria-label="Page guide" onClick={onGuide}>
              <i className="bi bi-question-lg" aria-hidden="true" />
            </button>
          </div>
          {hasMap && model.days.length ? (
            <DayPills className="trip-live-days" model={model} value={dayFilter} onChange={setDayFilter} />
          ) : null}
        </div>
      </div>

      <div className="trip-live-sheet">
        <div className="trip-live-handle" aria-hidden="true" />
        <HereNow tracking={tracking} model={model} now={now} emergencyShown={emergencyShown} />
        <TripPlan
          tracking={tracking}
          model={model}
          now={now}
          dayFilter={dayFilter}
          onDayChange={hasMap ? null : setDayFilter}
          onEditPickup={onEditPickup}
        />
        {children}
      </div>
    </div>
  );
}

function liveStatus(tracking, model) {
  const vehicle = model?.vehicle;
  if (model?.tripEnded) return { on: false, text: "Trip completed" };
  if (vehicle && !vehicle.stale) return { on: true, text: `Live · updated ${ageLabel(vehicle.minutesAgo)}` };
  if (vehicle) return { on: false, text: `Vehicle last seen ${ageLabel(vehicle.minutesAgo)}` };
  return { on: false, text: `Travel ${formatDateDMY(tracking.vehicleStartDate || tracking.travelDate, "-")}` };
}

// Only what is happening right now; the day by day plan is the card below it.
function HereNow({ tracking, model, now, emergencyShown }) {
  const liveDay = model?.days?.[Math.max(model.currentDayIndex, 0)] || null;
  const head = headline(tracking, model, now, liveDay);
  const driverPhone = String(tracking.driverPhone || "").replace(/[^\d+]/g, "");
  // At a stay, only the morning's first place is "next", not one left over from the evening before.
  const upNext = "upNext" in head ? head.upNext : model?.next !== head.stop ? model?.next : null;
  const showNext = upNext && !model.tripEnded;

  return (
    <section className="trip-now" aria-label="Right now">
      <div className="trip-now-head">
        {head.stop ? (
          <StopBadge stop={head.stop} size={44} />
        ) : (
          <span className="trip-stop-badge" style={{ width: 44, height: 44, background: "#0f2f4a", fontSize: 18 }}>
            <i className={`bi ${head.icon}`} aria-hidden="true" />
          </span>
        )}
        <div style={{ minWidth: 0 }}>
          <div className="trip-now-kicker">{head.kicker}</div>
          <div className="trip-now-title">{head.title}</div>
          {head.sub ? <div className="trip-now-sub">{head.sub}</div> : null}
        </div>
      </div>

      {showNext ? (
        <div className="trip-now-next">
          <i className="bi bi-arrow-right" aria-hidden="true" />
          <span><strong style={{ color: "#0f1f33" }}>Up next:</strong> {upNext.label}</span>
        </div>
      ) : null}

      {driverPhone || emergencyShown ? (
        <div className="trip-now-actions">
          {driverPhone ? (
            <a className="trip-now-action is-primary" href={`tel:${driverPhone}`}>
              <i className="bi bi-telephone-fill" aria-hidden="true" />Call driver
            </a>
          ) : null}
          {emergencyShown ? (
            <button
              type="button"
              className="trip-now-action is-danger"
              onClick={() => document.querySelector('[data-tour="emergency"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
            >
              <i className="bi bi-shield-exclamation" aria-hidden="true" />Emergency
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

// The day picked on the map, one line per place; All days lists every day. With no map to pick a
// day on, the day buttons sit at the top of this card instead.
function TripPlan({ tracking, model, now, dayFilter, onDayChange, onEditPickup }) {
  const allDays = model?.days || [];
  if (!allDays.length) {
    return (
      <section className="trip-plan trip-plan-empty" data-tour="trip-plan" aria-label="Trip plan">
        No trip plan has been configured for this booking yet.
      </section>
    );
  }
  const days = dayFilter === "all"
    ? allDays
    : [allDays[dayFilter] || allDays[Math.max(model.currentDayIndex, 0)]];

  const pickupAction = onEditPickup && canChangePickup(tracking) ? (
    <button
      type="button"
      className={`btn btn-sm trip-pickup-action ${tracking.pickupPoint ? "btn-outline-success" : "btn-success"}`}
      onClick={onEditPickup}
    >
      <i className="bi bi-geo-alt-fill me-1" aria-hidden="true" />
      {tracking.pickupPoint ? "Change pickup point / time" : "Set your pickup point & time"}
    </button>
  ) : null;

  return (
    <section className="trip-plan" data-tour="trip-plan" aria-label="Trip plan">
      {onDayChange ? <DayPills className="trip-plan-pills" model={model} value={dayFilter} onChange={onDayChange} /> : null}
      {days.map((day) => (
        <TripPlanDay key={day.key} detail={tracking} model={model} day={day} now={now} pickupAction={pickupAction} />
      ))}
    </section>
  );
}

function headline(tracking, model, now, day) {
  if (model?.tripEnded) {
    return {
      icon: "bi-flag-fill",
      kicker: "Trip completed",
      title: "Thank you for travelling with us",
      sub: tracking.tripEnded ? `Ended ${formatDateTimeDMY12(tracking.tripEnded.capturedAtIst, "")}` : "",
    };
  }
  if (model?.atStay) {
    return {
      icon: "bi-house-door-fill",
      kicker: model.atStay.back ? "Back at stay" : "Reached stay",
      title: model.atStay.hotel?.name || "Your stay",
      sub: sinceLabel(model.atStay.arrivedIst, now),
      upNext: model.atStay.next,
    };
  }
  if (model?.here) {
    return { stop: model.here, kicker: "Here now", title: model.here.label, sub: stopTimes(model.here, now) };
  }
  if (!model?.tripStarted) {
    if (tracking.driverReachedPickup) {
      return {
        icon: "bi-geo-alt-fill",
        kicker: "Driver at pickup",
        title: "Your driver has reached the pickup point",
        sub: `At ${formatClock(tracking.driverReachedPickup.capturedAtIst)}`,
      };
    }
    return {
      icon: "bi-calendar-event",
      kicker: "Trip starts",
      title: tracking.vehiclePickupDateTime
        ? formatDateTimeDMY12(tracking.vehiclePickupDateTime, "-")
        : formatDateDMY(tracking.vehicleStartDate || tracking.travelDate, "-"),
      sub: tracking.pickupPoint?.landmark ? `Pickup at ${tracking.pickupPoint.landmark}` : "Vehicle pickup",
    };
  }
  const last = model.lastVisited;
  const leftText = last
    ? `Left ${last.label} at ${formatClock((last.pickedUp || last.dropped)?.capturedAtIst)}`
    : tracking.tripStarted ? `Trip started ${formatClock(tracking.tripStarted.capturedAtIst)}` : "";
  if (model.next) {
    return { stop: model.next, kicker: "On the way to", title: model.next.label, sub: leftText };
  }
  if (model.leftStay) {
    return {
      icon: "bi-house-door",
      kicker: "Out from the stay",
      title: model.leftStay.hotel?.name || "Your stay",
      sub: `Left ${formatClock(model.leftStay.leftIst)}`,
    };
  }
  const stay = day?.tonight?.[0];
  if (!stay && day && day.index === model.days.length - 1) {
    return { icon: "bi-flag-fill", kicker: "On the way to", title: "Your drop point", sub: leftText };
  }
  return { icon: "bi-house-door-fill", kicker: "On the way to", title: stay ? stay.name : "Your stay", sub: leftText };
}
